import { Direction, FindRailTransportsRequest, RailTransportType } from '../../../bewotec/openapi'
import { handleError } from '../../../general/errorHandling'
import {
  useCruiseStore,
  useDetailsStore,
  useFlexFlightStore,
  useFlightStore,
  useRailStore,
  useUserStore
} from '../../../store'
import { appHelper, mapAgencyForRequest } from '../../../store/app/helper'
import { convertToExpitecDirection, mapRailOption } from '../rail/helper'
import { getDummyPaxAges } from './helper'
import {
  IFetchRailOption,
  IFetchRailOptionByDirection,
  IFindRailTransportationParameters,
  RailDirection,
} from './interfaces'

export const fetchRail = async () => {
  const railStore = useRailStore()

  railStore.resetRail()
  await railStore.fetchRailOptions()
}

export const fetchRailOptions = async () => {
  const userStore = useUserStore()
  const railStore = useRailStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()

  const cruiseId = cruiseStore.cruise?.id

  if (!cruiseId) {
    railStore.selectableRailOptions = { error: true }
    handleError({ error: new Error('Cruise ID is missing'), origin: 'rail - fetchRailOptions' })
    return 
  }

  const findRailTransportationParameters: IFindRailTransportationParameters = {
    cruiseId,
    agencyId: userStore.agencyId,
    priceModel: detailsStore.priceModel!,
    paxConfig: detailsStore.paxConfig!,
    transportationType: RailTransportType.RIT,
    childrenOlderThanFourteen: railStore.railAdults,
    origin: 'rail',
  }

  const railOptions = await railStore.findRailTransportation(findRailTransportationParameters)
  
  if (!!railOptions?.length) {
    const availableRailOptionsByDirection: IFetchRailOptionByDirection = {}
    
    for (const option of railOptions) {
      if (!availableRailOptionsByDirection[option.direction]) {
        availableRailOptionsByDirection[option.direction] = []
      } 

      const mappedOption = mapRailOption(option)
      availableRailOptionsByDirection[option.direction]?.push(mappedOption)
    }

    for (const direction of Object.keys(availableRailOptionsByDirection)) {
      const availableRailOptions: IFetchRailOption[] = availableRailOptionsByDirection?.[direction as Direction] ?? []

      availableRailOptions?.sort((a, b) => (a.railClassCode < b.railClassCode ? -1 : 1))
    }

    railStore.selectableRailOptions = availableRailOptionsByDirection
    setInitialRITDirection()
  } else {
    railStore.selectableRailOptions = { error: true }
  }
}

export const findRailTransportation = async (railParams: IFindRailTransportationParameters) => {
  const railStore = useRailStore()
  const flexFlightStore = useFlexFlightStore()
  const flightStore = useFlightStore()

  const paxAgeArray: number[] = getDummyPaxAges(
    railParams.paxConfig,
    railParams.childrenOlderThanFourteen,
    railParams.birthdates
  )

  try {
    const railTransportRequest: FindRailTransportsRequest = {
      cruiseId: railParams.cruiseId,
      agencyId: mapAgencyForRequest(railParams.agencyId),
      priceModelCode: railParams.priceModel,
      ages: paxAgeArray,
      type: railParams.transportationType,
    }

    if (!!railParams.gateway) {
      railTransportRequest.gateway = railParams.gateway
    }
    if (!!railParams.railDirection) {
      railTransportRequest.direction = railParams.railDirection
    }

    const bewotecRails = await appHelper.bewotecMicroServiceApi().findRailTransports(railTransportRequest)

    // TODO: clean up
    if (!bewotecRails.length) {
      if (railParams.origin === 'flexFlight') {
        flexFlightStore.selectableRailOptions = { error: true }
      } else if (railParams.origin === 'flight') {
        flightStore.selectableRailOptions = { error: true }
      } else {
        railStore.selectableRailOptions = { error: true }
      }
    }

    return bewotecRails
  } catch (error) {
    handleError({ error, origin: 'findRailTransportation' })

    if (railParams.origin === 'flexFlight') {
      flexFlightStore.selectableRailOptions = { error: true }
    } else if (railParams.origin === 'flight') {
      flightStore.selectableRailOptions = { error: true }
    } else {
      railStore.selectableRailOptions = { error: true }
    }
  }
}

const setInitialRITDirection = () => {
  const railStore = useRailStore()
  const selectableRailOptions = railStore.selectableRailOptions
  const isError = 'error' in selectableRailOptions

  if (!isError && !!selectableRailOptions[Direction.RT]?.length) {
    railStore.railDirection = 'both'
  } else if (!isError && !!selectableRailOptions[Direction.O]?.length) {
    railStore.railDirection = 'outbound'
  } else if (!isError && !!selectableRailOptions[Direction.R]?.length) {
    railStore.railDirection = 'inbound'
  } else {
    railStore.railDirection = 'none'
  }
}

export const selectRailDirection = async (direction: RailDirection) => {
  const railStore = useRailStore()

  if (railStore.railDirection !== direction) {
    railStore.resetSelectedRailOption()
    railStore.railDirection = direction
  }
}

export const selectRailOption = (optionId: string) => {
  const railStore = useRailStore()

  const direction = convertToExpitecDirection(railStore.railDirection)
  const selectableRailOptions = railStore.selectableRailOptions
  const hasSelectableRailOptions = !!selectableRailOptions && !('error' in selectableRailOptions)
  const options = hasSelectableRailOptions ? selectableRailOptions[direction] : []

  if(!!options?.length) {
    for (const option of options) {
      if (option !== null && 'id' in option && option.id === optionId) {
        railStore.selectedRailOption = option
        break
      }
    }
  }
}

export const resetRail = () => {
  const railStore = useRailStore()

  railStore.resetSelectedRailOption()

  railStore.railDirection = 'none'
  railStore.railAdults = 0
}

export const resetSelectedRailOption = () => {
  const railStore = useRailStore()

  railStore.selectedRailOptionId = ''
  railStore.selectedRailOption = null
}

export default {
  fetchRail,
  fetchRailOptions,
  findRailTransportation,
  selectRailDirection,
  selectRailOption,
  resetRail,
  resetSelectedRailOption
}

// ------- Exported for testing purposes only -------

export const forTestingPurposeOnly = {
  setInitialRITDirection
}
